import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Facebook, Instagram, MapPin, Phone } from 'lucide-react';
import { companyInfo, footerLinks, contactInfo } from '../data/mock';

const Footer = () => {
  const socialIcons = {
    LinkedIn: Linkedin,
    Twitter: Twitter,
    Facebook: Facebook,
    Instagram: Instagram
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Logo and description */}
          <div className="lg:col-span-1">
            <div data-testid="footer-brand" className="flex flex-wrap items-center gap-3 mb-6">
              <div className="h-14 w-14 bg-[#E30022] rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xl font-bold font-serif">RS</span>
              </div>
              <span data-testid="footer-company-name" className="text-white font-serif text-xl">Recrutement<br/>Suisse</span>
            </div>
            <p data-testid="footer-company-description" className="text-gray-400 text-sm leading-relaxed">
              {companyInfo.description}
            </p>
          </div>

          {/* Links */}
          {Object.values(footerLinks).map((section, index) => (
            <div key={index}>
              <h4 data-testid={`footer-section-${index}-title`} className="font-semibold text-lg mb-4">{section.title}</h4>
              <ul className="space-y-3">
                {section.links.map((link, idx) => (
                  <li key={idx}>
                    <Link
                      to={link.href}
                      data-testid={`footer-section-${index}-${idx}-link`}
                      className="text-gray-400 hover:text-[#E30022] transition-colors text-sm"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Offices */}
        <div className="mt-16 pt-8 border-t border-gray-800">
          <h4 data-testid="footer-offices-title" className="font-semibold text-lg mb-6">Nos bureaux</h4>
          <div className="grid sm:grid-cols-3 gap-6">
            {contactInfo.offices.map((office, index) => (
              <div key={index} className="text-sm">
                <h5 data-testid={`footer-office-${index}-city`} className="font-medium text-white mb-2 flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-[#E30022]" />
                  {office.city}
                </h5>
                <p data-testid={`footer-office-${index}-address`} className="text-gray-400 mb-1">{office.address}</p>
                <p data-testid={`footer-office-${index}-phone`} className="text-gray-400 flex items-center gap-2">
                  <Phone className="h-3 w-3" />
                  {office.phone}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Social Links */}
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {contactInfo.social.map((social, index) => {
              const Icon = socialIcons[social.name];
              return (
                <span 
                  key={index}
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center cursor-default"
                  aria-label={social.name}
                  data-testid={`footer-social-${social.name.toLowerCase()}`}
                >
                  <Icon className="h-5 w-5" />
                </span>
              );
            })}
          </div>
          <p data-testid="footer-copyright" className="text-gray-400 text-sm">
            © {new Date().getFullYear()} {companyInfo.name}. Tous droits réservés.
          </p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-[#E30022]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <Link to="/politique-confidentialite" data-testid="footer-privacy-link" className="hover:underline">Politique de confidentialité</Link>
            <Link to="/conditions-utilisation" data-testid="footer-terms-link" className="hover:underline">Conditions d'utilisation</Link>
            <Link to="/cookies" data-testid="footer-cookies-link" className="hover:underline">Paramètres des cookies</Link>
            <Link to="/mentions-legales" data-testid="footer-legal-link" className="hover:underline">Mentions légales</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
